import { useState, type FormEvent } from 'react';
import { Text } from '@/components/ui/Text';
import { Button } from '@/components/ui/Button';
import { PasswordInput } from '@/components/ui/PasswordInput';
import { PasswordPolicyHint } from '@/pages/Signup/PasswordPolicyHint';
import { fetchRegisterPolicy, type RegisterPolicy } from '@/lib/auth/register-api';
import { postAcceptInvite, type InviteAcceptOutcome } from '@/lib/auth/invite-api';
import { useQuery } from '@tanstack/react-query';

type Phase =
  | { tag: 'form' }
  | { tag: 'submitting' }
  | { tag: 'done'; outcome: InviteAcceptOutcome };

function getToken(): string | null {
  return new URLSearchParams(window.location.search).get('token');
}

function Shell({ children }: { children: React.ReactNode }) {
  return (
    <div className="min-h-screen bg-bg text-text flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-4">{children}</div>
    </div>
  );
}

/**
 * "Set your password" for an invited user (`/invite/accept?token=…`).
 *
 * ## Why this page exists at all
 * The invite email's link used to land on the ordinary app, which boots with Keycloak
 * `onLoad: 'login-required'` and therefore redirected the recipient to a sign-in form — asking for
 * credentials from the one person who by definition has none. This page is mounted BEFORE Keycloak
 * (see `main.tsx`), exactly like `/verify` and `/signup`, so the token can be used anonymously.
 *
 * The password rules come from the same BE policy the signup flow uses, so an invited user is held
 * to the same rules as a self-registering one, and the two cannot drift apart.
 */
export function InviteAcceptApp() {
  const token = getToken();
  const [phase, setPhase] = useState<Phase>({ tag: 'form' });
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [touched, setTouched] = useState(false);

  const policyQ = useQuery<RegisterPolicy>({
    queryKey: ['registerPolicy'],
    queryFn: fetchRegisterPolicy,
    staleTime: 5 * 60_000,
  });
  const policy = policyQ.data?.passwordPolicy;

  if (!token) {
    return (
      <Shell>
        <Text variant="headline-sm" as="h1">This link is incomplete</Text>
        <Text variant="body-md" color="muted">
          No invite token was found in the link. Open the most recent invitation email and use the
          button there, or ask your administrator to send a new invite.
        </Text>
      </Shell>
    );
  }

  if (phase.tag === 'done' && phase.outcome.ok) {
    return (
      <Shell>
        <Text variant="headline-sm" as="h1">Your password is set</Text>
        <Text variant="body-md" color="muted">
          You can now sign in with your email address and the password you just chose.
        </Text>
        <Button onClick={() => { window.location.href = '/'; }}>Go to sign in</Button>
      </Shell>
    );
  }

  const failed = phase.tag === 'done' && !phase.outcome.ok ? phase.outcome : null;
  // A dead token cannot be retried in place — the only way forward is a fresh invite. A rejected
  // password can be, so the form stays on screen for that one.
  const tokenDead = failed?.reason === 'invalid_or_expired';

  if (tokenDead) {
    return (
      <Shell>
        <Text variant="headline-sm" as="h1">This invite link has expired</Text>
        <Text variant="body-md" color="muted">
          Invite links are single-use and time-limited. Ask your administrator to send a new
          invitation, then use the link in that email.
        </Text>
      </Shell>
    );
  }

  const mismatch = touched && confirm.length > 0 && password !== confirm;
  const canSubmit =
    password.length > 0 && password === confirm && phase.tag !== 'submitting' && !!policy;

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!canSubmit || !token) return;
    setPhase({ tag: 'submitting' });
    const outcome = await postAcceptInvite(token, password);
    setPhase({ tag: 'done', outcome });
  }

  return (
    <Shell>
      <div className="space-y-1">
        <Text variant="headline-sm" as="h1">Set your password</Text>
        <Text variant="body-md" color="muted">
          Choose a password for your Entiti account. You'll use it with the email address your
          invitation was sent to.
        </Text>
      </div>

      <form onSubmit={onSubmit} className="space-y-4" noValidate>
        <div className="space-y-1.5">
          <Text as="label" htmlFor="invite-password" variant="label-lg">New password</Text>
          <PasswordInput
            id="invite-password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => { setPassword(e.target.value); setTouched(true); }}
          />
          {policy && <PasswordPolicyHint policy={policy} value={password} touched={touched} />}
        </div>

        <div className="space-y-1.5">
          <Text as="label" htmlFor="invite-confirm" variant="label-lg">Confirm password</Text>
          <PasswordInput
            id="invite-confirm"
            autoComplete="new-password"
            value={confirm}
            aria-invalid={mismatch || undefined}
            aria-describedby={mismatch ? 'invite-confirm-error' : undefined}
            onChange={(e) => { setConfirm(e.target.value); setTouched(true); }}
          />
          {mismatch && (
            <Text id="invite-confirm-error" variant="body-sm" color="danger" role="alert">
              Both passwords must match.
            </Text>
          )}
        </div>

        {failed && (
          <Text variant="body-sm" color="danger" role="alert">
            {failed.reason === 'network_error'
              ? "We couldn't reach the server. Check your connection and try again."
              : (failed.message ?? 'That password was rejected. Try a different one.')}
          </Text>
        )}

        <Button type="submit" disabled={!canSubmit} className="w-full">
          {phase.tag === 'submitting' ? 'Setting your password…' : 'Set password'}
        </Button>
      </form>
    </Shell>
  );
}
